"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuthStore } from "@/app/lib/store/authStore";
import { hasVaultAccess } from "@/app/lib/utils/subscriptionAccess";

// Helper function to check if current path is a public auth page
const isPublicAuthPage = (pathname: string | null): boolean => {
  return pathname?.startsWith("/login") || 
         pathname?.startsWith("/register") || 
         pathname?.startsWith("/fanbasis-register") ||
         pathname?.startsWith("/forgot-password") ||
         pathname?.startsWith("/reset-password") ||
         false;
};

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, _hasHydrated, user, logout, setAccessDenied } = useAuthStore();

  useEffect(() => {
    // Wait for hydration to complete before checking auth
    if (!_hasHydrated) {
      return;
    }

    // Allow access to public auth pages without authentication
    if (isPublicAuthPage(pathname)) {
      return;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      router.push("/login");
      return;
    }

    // Check subscription access on rehydration — same logic for all roles
    if (isAuthenticated && user && !hasVaultAccess(user)) {
      setAccessDenied(true);
      logout();
      router.push("/login");
    }
  }, [isAuthenticated, _hasHydrated, pathname, router, user, logout, setAccessDenied]);

  // Show public auth pages without auth check
  if (isPublicAuthPage(pathname)) {
    return <>{children}</>;
  }

  // Show loading while hydrating or checking auth
  if (!_hasHydrated || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Render children if authenticated
  return <>{children}</>;
}
